import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useModal } from '../context/ModalContext';
import { translations } from '../translations';
import api from '../utils/api';
import { compareArabic, normalizeArabic } from '../utils/arabicNormalize';
import html2pdf from 'html2pdf.js';
import { trackLead, trackInitiateCheckout, trackPurchase } from '../utils/facebookPixel';
import { pushFormView, pushFormStart, pushSubmitAttempt, pushLead } from '../utils/dataLayer';

const Book = () => {
  const { language, isRTL } = useLanguage();
  const { alert } = useModal();
  const t = translations[language];
  const [formData, setFormData] = useState({
    bookingType: 'anywhere',
    tripType: 'oneWay',
    pickupLocation: '',
    dropoffLocation: '',
    date: '',
    time: '',
    returnDate: '',
    returnTime: '',
    passengers: 1,
    luggage: 0,
    carType: '',
    fullName: '',
    mobile: '',
    email: '',
    whatsapp: '',
    notes: '',
    distance: 50
  });
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [step, setStep] = useState(1);
  const [presets, setPresets] = useState([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [dropoffLocations, setDropoffLocations] = useState([]);
  const [_selectedPreset, setSelectedPreset] = useState(null);
  const [presetPrice, setPresetPrice] = useState(null);
  const [routePresets, setRoutePresets] = useState([]); // All presets for the selected route
  const [availableCars, setAvailableCars] = useState([]); // Cars filtered by available presets
  const [_useCustomPickup, setUseCustomPickup] = useState(false);
  const [_useCustomDropoff, setUseCustomDropoff] = useState(false);
  const [_loadingPresets, setLoadingPresets] = useState(false);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [filteredPickupSuggestions, setFilteredPickupSuggestions] = useState([]);
  const [filteredDropoffSuggestions, setFilteredDropoffSuggestions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [paymentOption, setPaymentOption] = useState(''); // 'payNow', 'payCash', 'contactSupport'
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [quickPayMethod, setQuickPayMethod] = useState(''); // Selected payment method for quick pay
  const [quickPayProofImage, setQuickPayProofImage] = useState(null); // Payment proof image
  const [policyAgreed, setPolicyAgreed] = useState(false); // User must check manually in step 1
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupDropdownRef = useRef(null);
  const dropoffDropdownRef = useRef(null);
  const formRef = useRef(null);
  const formViewFiredRef = useRef(false);
  const formStartFiredRef = useRef(false);

  // Icon mapping for payment methods
  const getPaymentIcon = (methodId) => {
    const icons = {
      bankTransfer: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      vodafone: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#E60000' }}>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ),
      orange: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#FF6600' }}>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.4" />
        </svg>
      ),
      etisalat: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#00ADEF' }}>
          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <rect x="8" y="10" width="8" height="4" fill="currentColor" />
        </svg>
      ),
      instapay: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      cash: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    };
    return icons[methodId] || icons.bankTransfer;
  };

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const res = await api.get('/cars');
        setCars(res.data);
      } catch (error) {
        console.error('Error fetching cars:', error);
      }
    };
    fetchCars();

    const fetchPresets = async () => {
      try {
        setLoadingPresets(true);
        const res = await api.get('/route-presets');
        setPresets(res.data);

        // Get unique pickup locations
        const pickups = [...new Set(res.data.map(p => p.pickupLocation))].sort();
        setPickupLocations(pickups);
      } catch (error) {
        console.error('Error fetching presets:', error);
      } finally {
        setLoadingPresets(false);
      }
    };
    fetchPresets();

    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings');
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();

    const fetchPaymentMethods = async () => {
      try {
        const res = await api.get('/payment-methods');
        const methods = res.data.map(method => ({
          id: method.id,
          title: language === 'en' ? method.title.en : method.title.ar,
          description: language === 'en' ? method.description.en : method.description.ar,
          details: language === 'en' ? method.details.en : method.details.ar,
          icon: getPaymentIcon(method.id),
          isActive: method.isActive
        }));
        setPaymentMethods(methods);
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    };
    fetchPaymentMethods();

    // Pre-fill user data if logged in
    const token = localStorage.getItem('token');
    if (token) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user) {
        setFormData(prev => ({
          ...prev,
          fullName: user.name || prev.fullName,
          mobile: user.phone || prev.mobile,
          email: user.email || prev.email
        }));
      }
    }
  }, [language]);

  // Filter pickup suggestions based on input
  const filterPickupSuggestions = (query) => {
    if (!query || query.trim() === '') {
      setFilteredPickupSuggestions(pickupLocations);
      return;
    }
    const normalizedQuery = normalizeArabic(query.toLowerCase());
    const filtered = pickupLocations.filter(location => {
      const normalizedLocation = normalizeArabic(location.toLowerCase());
      return normalizedLocation.includes(normalizedQuery);
    });
    setFilteredPickupSuggestions(filtered);
  };

  // Filter dropoff suggestions based on input and selected pickup
  const filterDropoffSuggestions = (query) => {
    if (!query || query.trim() === '') {
      setFilteredDropoffSuggestions(dropoffLocations);
      return;
    }
    const normalizedQuery = normalizeArabic(query.toLowerCase());
    const filtered = dropoffLocations.filter(location => {
      const normalizedLocation = normalizeArabic(location.toLowerCase());
      return normalizedLocation.includes(normalizedQuery);
    });
    setFilteredDropoffSuggestions(filtered);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // Validate date - cannot be in the past
    if (name === 'date') {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        await alert(
          language === 'en'
            ? 'Cannot select past dates. Please choose today or a future date.'
            : 'لا يمكن اختيار التواريخ السابقة. يرجى اختيار اليوم أو تاريخ في المستقبل.',
          language === 'en' ? 'Invalid Date' : 'تاريخ غير صحيح',
          'warning'
        );
        return; // Don't update the date
      }
    }

    // Validate return date - must be on or after departure date
    if (name === 'returnDate') {
      if (formData.date && value) {
        const departureDate = new Date(formData.date);
        const returnDate = new Date(value);

        if (returnDate < departureDate) {
          await alert(
            language === 'en'
              ? 'Return date must be on or after the departure date.'
              : 'يجب أن يكون تاريخ العودة في نفس تاريخ الذهاب أو بعده.',
            language === 'en' ? 'Invalid Date' : 'تاريخ غير صحيح',
            'warning'
          );
          return; // Don't update the return date
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle pickup location change
    if (name === 'pickupLocation') {
      // Filter suggestions as user types
      filterPickupSuggestions(value);
      setShowPickupSuggestions(true);

      // Filter dropoff locations based on pickup (using Arabic normalization for smart matching)
      const dropoffs = presets
        .filter(p => compareArabic(p.pickupLocation, value) && p.isActive)
        .map(p => p.dropoffLocation);
      const uniqueDropoffs = [...new Set(dropoffs)].sort();
      setDropoffLocations(uniqueDropoffs);
      setSelectedPreset(null);
      setPresetPrice(null);
      setFormData(prev => ({ ...prev, dropoffLocation: '' }));
      setShowDropoffSuggestions(false);
    }

    // Handle dropoff location change
    if (name === 'dropoffLocation') {
      // Filter suggestions as user types
      filterDropoffSuggestions(value);
      setShowDropoffSuggestions(true);

      // Find matching preset (use value for dropoff, formData.pickupLocation for pickup)
      if (formData.pickupLocation && value) {
        findPreset(formData.pickupLocation, value);
      }
    }

    // Handle car type change - find preset for selected car type
    if (name === 'carType' && formData.pickupLocation && formData.dropoffLocation) {
      if (routePresets.length > 0) {
        // Try to find preset for specific car type first
        let preset = routePresets.find(p => p.carType === value);

        // If not found, try 'all' type
        if (!preset) {
          preset = routePresets.find(p => p.carType === 'all');
        }

        if (preset) {
          setSelectedPreset(preset);
          setPresetPrice(preset.price);
        } else {
          setSelectedPreset(null);
          setPresetPrice(null);
        }
      } else {
        // If no route presets loaded yet, fetch them
        findPreset(formData.pickupLocation, formData.dropoffLocation, value);
      }
    }
  };

  const handlePickupSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      pickupLocation: location,
      dropoffLocation: '' // Clear dropoff when pickup changes
    }));
    setShowPickupSuggestions(false);

    // Filter dropoff locations based on selected pickup
    const dropoffs = presets
      .filter(p => compareArabic(p.pickupLocation, location) && p.isActive)
      .map(p => p.dropoffLocation);
    const uniqueDropoffs = [...new Set(dropoffs)].sort();
    setDropoffLocations(uniqueDropoffs);
    setFilteredDropoffSuggestions(uniqueDropoffs);
    setSelectedPreset(null);
    setPresetPrice(null);
    setShowDropoffSuggestions(false);
  };

  const handleDropoffSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      dropoffLocation: location
    }));
    setShowDropoffSuggestions(false);

    // Immediately fetch all presets for this route when selecting from dropdown
    const currentPickup = formData.pickupLocation;
    if (currentPickup && location) {
      fetchRoutePresets(currentPickup, location);
    }
  };

  // Fetch all presets for a route (all car types)
  const fetchRoutePresets = async (pickup, dropoff) => {
    if (!pickup || !dropoff) {
      setRoutePresets([]);
      setAvailableCars(cars); // Show all cars if no route selected
      setSelectedPreset(null);
      setPresetPrice(null);
      return;
    }

    try {
      const res = await api.get('/route-presets/route', {
        params: {
          pickupLocation: pickup.trim(),
          dropoffLocation: dropoff.trim()
        }
      });

      if (res.data && res.data.length > 0) {
        const presets = res.data;
        setRoutePresets(presets);

        // Get unique car types from presets (including 'all')
        const presetCarTypes = [...new Set(presets.map(p => p.carType))];

        // Filter cars: show cars that have a preset (either specific type or 'all')
        const filtered = cars.filter(car => {
          // If there's an 'all' preset, show all cars
          if (presetCarTypes.includes('all')) {
            return true;
          }
          // Otherwise, only show cars that have a specific preset
          return presetCarTypes.includes(car.type);
        });

        setAvailableCars(filtered);

        // If a car type is already selected, find and set its price
        if (formData.carType) {
          // Try to find preset for specific car type first
          let carPreset = presets.find(p => p.carType === formData.carType);

          // If not found, try 'all' type
          if (!carPreset) {
            carPreset = presets.find(p => p.carType === 'all');
          }

          if (carPreset) {
            setSelectedPreset(carPreset);
            setPresetPrice(carPreset.price);
          } else {
            setSelectedPreset(null);
            setPresetPrice(null);
          }
        } else {
          // If no car selected, clear preset
          setSelectedPreset(null);
          setPresetPrice(null);
        }
      } else {
        setRoutePresets([]);
        setAvailableCars(cars); // Show all cars if no presets found
        setSelectedPreset(null);
        setPresetPrice(null);
      }
    } catch (error) {
      setRoutePresets([]);
      setAvailableCars(cars);
      setSelectedPreset(null);
      setPresetPrice(null);
    }
  };

  const findPreset = async (pickup, dropoff, carType = null) => {
    if (!pickup || !dropoff) {
      setSelectedPreset(null);
      setPresetPrice(null);
      return;
    }

    // Fetch all presets for this route
    await fetchRoutePresets(pickup, dropoff);
  };

  // Debounced preset lookup when user types (not when selecting from dropdown)
  useEffect(() => {
    // Only check for presets if both locations are filled
    if (formData.pickupLocation && formData.dropoffLocation) {
      const timer = setTimeout(() => {
        // Fetch all presets for this route
        fetchRoutePresets(formData.pickupLocation, formData.dropoffLocation);
      }, 800); // Wait 800ms after user stops typing

      return () => clearTimeout(timer);
    } else if (!formData.pickupLocation || !formData.dropoffLocation) {
      // Clear preset if locations are incomplete
      setRoutePresets([]);
      setAvailableCars(cars);
      setSelectedPreset(null);
      setPresetPrice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pickupLocation, formData.dropoffLocation]);

  // Update price when car type changes and route presets are available
  useEffect(() => {
    if (formData.carType && routePresets.length > 0) {
      // Try to find preset for specific car type first
      let preset = routePresets.find(p => p.carType === formData.carType);

      // If not found, try 'all' type
      if (!preset) {
        preset = routePresets.find(p => p.carType === 'all');
      }

      if (preset) {
        setSelectedPreset(preset);
        setPresetPrice(preset.price);
      } else {
        setSelectedPreset(null);
        setPresetPrice(null);
        // If no preset price, reset payment option if Pay Now was selected
        if (paymentOption === 'payNow') {
          setPaymentOption('');
          setQuickPayMethod('');
          setQuickPayProofImage(null);
        }
      }
    } else if (!formData.carType) {
      setSelectedPreset(null);
      setPresetPrice(null);
    }
  }, [formData.carType, routePresets]);

  // Reset payment option if preset price becomes unavailable
  useEffect(() => {
    if (!presetPrice && paymentOption === 'payNow') {
      setPaymentOption('');
      setQuickPayMethod('');
      setQuickPayProofImage(null);
    }
  }, [presetPrice, paymentOption]);

  // Initialize available cars with all cars
  useEffect(() => {
    setAvailableCars(cars);
  }, [cars]);

  // Initialize filtered suggestions when presets or locations change
  useEffect(() => {
    setFilteredPickupSuggestions(pickupLocations);
  }, [pickupLocations]);

  useEffect(() => {
    setFilteredDropoffSuggestions(dropoffLocations);
  }, [dropoffLocations]);

  // GTM/GA4: form_view when form enters viewport (once per mount)
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !formViewFiredRef.current) {
          formViewFiredRef.current = true;
          pushFormView('booking', { page_path: '/book' });
        }
      },
      { threshold: 0.2, rootMargin: '0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // GTM/GA4: form_start on first interaction with any form field (once)
  const handleFormStart = () => {
    if (formStartFiredRef.current) return;
    formStartFiredRef.current = true;
    pushFormStart('booking', { page_path: '/book' });
  };

  // Calculate displayed price based on trip type and settings
  const getDisplayPrice = (basePrice) => {
    if (!basePrice) return null;
    if (formData.tripType === 'roundTrip' && settings) {
      return basePrice * (settings.roundTripMultiplier || 2);
    }
    return basePrice;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (step !== 4) {
      nextStep();
      return;
    }

    // GTM/GA4: submit_attempt – fired when user clicks submit (even if validation fails)
    pushSubmitAttempt('booking', { page_path: '/book' });

    if (!paymentOption) {
      await alert(
        language === 'en' ? 'Please select a payment option' : 'يرجى اختيار طريقة الدفع',
        language === 'en' ? 'Required' : 'مطلوب',
        'warning'
      );
      return;
    }

    // Validate quick pay fields if Pay Now is selected
    if (paymentOption === 'payNow') {
      if (!quickPayMethod) {
        await alert(
          language === 'en' ? 'Please select a payment method' : 'يرجى اختيار طريقة الدفع',
          language === 'en' ? 'Required' : 'مطلوب',
          'warning'
        );
        return;
      }
      if (!quickPayProofImage) {
        await alert(
          language === 'en' ? 'Please upload payment proof' : 'يرجى رفع إثبات الدفع',
          language === 'en' ? 'Required' : 'مطلوب',
          'warning'
        );
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare booking data
      const bookingData = {
        ...formData,
        presetPrice: presetPrice || null, // Send preset price if exists, otherwise null
        paymentOption: paymentOption // Include payment option
      };

      // If Pay Now is selected, include payment details
      if (paymentOption === 'payNow') {
        bookingData.paymentMethod = quickPayMethod;
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      Object.keys(bookingData).forEach(key => {
        if (bookingData[key] !== null && bookingData[key] !== undefined && bookingData[key] !== '') {
          formDataToSend.append(key, bookingData[key]);
        }
      });

      // Append payment proof image if Pay Now is selected
      if (paymentOption === 'payNow' && quickPayProofImage) {
        formDataToSend.append('paymentProofImage', quickPayProofImage);
      }

      const response = await api.post('/bookings', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Save booking data and show success page
      setBookingData(response.data);
      setSuccess(true);

      // GTM/GA4: lead conversion – ONLY after confirmed server success (201)
      const bookingValue = getDisplayPrice(presetPrice) || 0;
      pushLead('booking', { value: bookingValue, currency: 'EGP', page_path: '/book' });

      // Track Facebook Pixel events on successful booking
      trackLead({
        content_name: 'Booking Request',
        content_category: 'Limousine Booking',
        value: bookingValue,
        currency: 'EGP'
      });

      // If payment was made, track Purchase event
      if (paymentOption === 'payNow') {
        trackPurchase({
          value: bookingValue,
          currency: 'EGP',
          content_type: 'service',
          content_name: `${formData.carType} - ${formData.pickupLocation} to ${formData.dropoffLocation}`,
          num_items: 1
        });
      }

      // Scroll to top when showing success message
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error submitting booking' : 'خطأ في إرسال الحجز'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Validate current step before proceeding
  const isStepValid = () => {
    if (step === 1) {
      // Step 1: Trip Details + Policy agreement
      return (
        formData.bookingType &&
        formData.tripType &&
        formData.pickupLocation?.trim() &&
        formData.dropoffLocation?.trim() &&
        formData.passengers > 0 &&
        formData.luggage >= 0 &&
        formData.carType &&
        policyAgreed
      );
    } else if (step === 2) {
      // Step 2: Schedule
      if (!formData.date || !formData.time) {
        return false;
      }

      // Validate date is not in the past
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        return false;
      }

      // For round trip, validate return date
      if (formData.tripType === 'roundTrip' && formData.returnDate) {
        const departureDate = new Date(formData.date);
        const returnDate = new Date(formData.returnDate);

        if (returnDate < departureDate) {
          return false;
        }
      }

      return true;
    } else if (step === 3) {
      // Step 3: Contact Info
      return (
        formData.fullName?.trim() &&
        formData.mobile?.trim()
      );
    } else if (step === 4) {
      // Step 4: Payment
      if (!paymentOption) return false;
      if (paymentOption === 'payNow') {
        return quickPayMethod && quickPayProofImage;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (isStepValid()) {
      const newStep = Math.min(step + 1, 4);
      setStep(newStep);
      // Track InitiateCheckout when moving to payment step
      if (newStep === 4) {
        trackInitiateCheckout({
          value: getDisplayPrice(presetPrice) || 0,
          currency: 'EGP',
          content_name: `${formData.carType} - ${formData.pickupLocation} to ${formData.dropoffLocation}`,
          num_items: 1
        });
      }
    }
  };
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  // Function to print receipt as PDF
  const printReceipt = () => {
    const element = document.getElementById('receipt-content');
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `booking-receipt-${bookingData?._id || 'receipt'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get booking type label
  const getBookingTypeLabel = (type) => {
    const types = {
      fromAirport: language === 'en' ? 'From Airport' : 'من المطار',
      toAirport: language === 'en' ? 'To Airport' : 'إلى المطار',
      insideCairo: language === 'en' ? 'Inside Cairo' : 'داخل القاهرة',
      anywhere: language === 'en' ? 'Anywhere' : 'أي مكان'
    };
    return types[type] || type;
  };

  // Get payment method label
  const getPaymentMethodLabel = (method, option) => {
    // If paymentOption is payCash, return Cash on Delivery
    if (option === 'payCash') {
      return language === 'en' ? 'Cash on Delivery' : 'الدفع عند الاستلام';
    }
    // If paymentOption is contactSupport, return Contact Support
    if (option === 'contactSupport') {
      return language === 'en' ? 'Contact Support' : 'اتصل بالدعم';
    }
    // If paymentOption is payNow and method is provided, return method name
    if (option === 'payNow' && method) {
      const methods = {
        bankTransfer: language === 'en' ? 'Bank Transfer' : 'تحويل بنكي',
        vodafone: 'Vodafone Cash',
        orange: 'Orange Money',
        etisalat: 'Etisalat Cash',
        instapay: 'InstaPay',
        cash: language === 'en' ? 'Cash' : 'نقدي'
      };
      return methods[method] || method;
    }
    // Default
    return language === 'en' ? 'Not Specified' : 'غير محدد';
  };

  if (success && bookingData) {
    return (
      <div className="min-h-screen bg-blackC py-16 pt-32 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-primary/10 border border-primary flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="font-display text-4xl text-cream mb-4">
              {language === 'en' ? 'Booking Confirmed!' : 'تم تأكيد الحجز!'}
            </h2>
            <p className="text-gray-silver mb-6">
              {language === 'en'
                ? 'Your booking has been confirmed. You can print or download the receipt below.'
                : 'تم تأكيد حجزك. يمكنك طباعة أو تحميل الإيصال أدناه.'}
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={printReceipt}
                className="btn-primary flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                {language === 'en' ? 'Print / Download PDF' : 'طباعة / تحميل PDF'}
              </button>
              <button
                onClick={() => {
                  setSuccess(false);
                  setBookingData(null);
                  setStep(1);
                  setPaymentOption('');
                  setQuickPayMethod('');
                  setQuickPayProofImage(null);
                  setFormData({
                    bookingType: 'fromAirport',
                    tripType: 'oneWay',
                    pickupLocation: '',
                    dropoffLocation: '',
                    date: '',
                    time: '',
                    returnDate: '',
                    returnTime: '',
                    passengers: 1,
                    luggage: 0,
                    carType: '',
                    fullName: '',
                    mobile: '',
                    email: '',
                    whatsapp: '',
                    notes: '',
                    distance: 50
                  });
                }}
                className="bg-charcoal border border-gray-dark text-cream px-6 py-3 rounded-lg hover:bg-gray-dark transition-colors"
              >
                {language === 'en' ? 'Book Another Ride' : 'احجز رحلة أخرى'}
              </button>
            </div>
          </div>

          {/* Receipt Content */}
          <div id="receipt-content" className="bg-white p-8 rounded-lg shadow-xl" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Lobster", cursive' }}>
                Global Limousine
              </h1>
              <p className="text-gray-600">{language === 'en' ? 'Booking Receipt' : 'إيصال الحجز'}</p>
            </div>

            {/* Booking Details */}
            <div className="space-y-6 mb-8">
              {/* Booking ID */}
              <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <span className="text-gray-600 font-semibold">{language === 'en' ? 'Booking ID' : 'رقم الحجز'}:</span>
                <span className="text-gray-900 font-mono text-lg">{bookingData._id}</span>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{language === 'en' ? 'Customer Information' : 'معلومات العميل'}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">{language === 'en' ? 'Name' : 'الاسم'}:</span>
                    <span className="text-gray-900 font-semibold ml-2">{bookingData.fullName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">{language === 'en' ? 'Mobile' : 'الجوال'}:</span>
                    <span className="text-gray-900 font-semibold ml-2">{bookingData.mobile}</span>
                  </div>
                  {bookingData.email && (
                    <div>
                      <span className="text-gray-600">{language === 'en' ? 'Email' : 'البريد الإلكتروني'}:</span>
                      <span className="text-gray-900 font-semibold ml-2">{bookingData.email}</span>
                    </div>
                  )}
                  {bookingData.whatsapp && (
                    <div>
                      <span className="text-gray-600">WhatsApp:</span>
                      <span className="text-gray-900 font-semibold ml-2">{bookingData.whatsapp}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Trip Details */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{language === 'en' ? 'Trip Details' : 'تفاصيل الرحلة'}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Booking Type' : 'نوع الحجز'}:</span>
                    <span className="text-gray-900 font-semibold">{getBookingTypeLabel(bookingData.bookingType)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Trip Type' : 'نوع الرحلة'}:</span>
                    <span className="text-gray-900 font-semibold">
                      {bookingData.tripType === 'oneWay'
                        ? (language === 'en' ? 'One Way' : 'ذهاب فقط')
                        : (language === 'en' ? 'Round Trip' : 'ذهاب وإياب')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Pickup Location' : 'موقع الاستلام'}:</span>
                    <span className="text-gray-900 font-semibold text-right">{bookingData.pickupLocation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Drop-off Location' : 'موقع الوصول'}:</span>
                    <span className="text-gray-900 font-semibold text-right">{bookingData.dropoffLocation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Date' : 'التاريخ'}:</span>
                    <span className="text-gray-900 font-semibold">{formatDate(bookingData.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Time' : 'الوقت'}:</span>
                    <span className="text-gray-900 font-semibold">{bookingData.time}</span>
                  </div>
                  {bookingData.returnDate && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{language === 'en' ? 'Return Date' : 'تاريخ العودة'}:</span>
                        <span className="text-gray-900 font-semibold">{formatDate(bookingData.returnDate)}</span>
                      </div>
                      {bookingData.returnTime && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{language === 'en' ? 'Return Time' : 'وقت العودة'}:</span>
                          <span className="text-gray-900 font-semibold">{bookingData.returnTime}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Passengers' : 'الركاب'}:</span>
                    <span className="text-gray-900 font-semibold">{bookingData.passengers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Luggage' : 'الأمتعة'}:</span>
                    <span className="text-gray-900 font-semibold">{bookingData.luggage}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Car Type' : 'نوع السيارة'}:</span>
                    <span className="text-gray-900 font-semibold">{bookingData.carType}</span>
                  </div>
                  {bookingData.notes && (
                    <div className="pt-3 border-t border-gray-200">
                      <span className="text-gray-600 block mb-2">{language === 'en' ? 'Notes' : 'ملاحظات'}:</span>
                      <span className="text-gray-900">{bookingData.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="pt-4 border-t-2 border-gray-300">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{language === 'en' ? 'Payment Information' : 'معلومات الدفع'}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{language === 'en' ? 'Payment Method' : 'طريقة الدفع'}:</span>
                    <span className="text-gray-900 font-semibold">{getPaymentMethodLabel(bookingData.paymentMethod, bookingData.paymentOption)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="text-xl font-bold text-gray-900">{language === 'en' ? 'Total Amount' : 'المبلغ الإجمالي'}:</span>
                    <span className="text-2xl font-bold text-primary">{bookingData.totalCost?.toLocaleString() || 0} EGP</span>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'Status' : 'الحالة'}:</span>
                  <span className={`px-4 py-2 rounded-full font-semibold ${bookingData.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                    bookingData.status === 'Confirmed' ? 'bg-green-100 text-green-800' :
                      bookingData.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                    }`}>
                    {bookingData.status === 'Pending' ? (language === 'en' ? 'Pending' : 'قيد الانتظار') :
                      bookingData.status === 'Confirmed' ? (language === 'en' ? 'Confirmed' : 'مؤكد') :
                        bookingData.status === 'Completed' ? (language === 'en' ? 'Completed' : 'مكتمل') :
                          (language === 'en' ? 'Cancelled' : 'ملغي')}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center pt-6 border-t-2 border-gray-300 text-xs text-gray-500">
              <p>{language === 'en' ? 'Thank you for choosing Global Limousine!' : 'شكراً لاختيارك Global Limousine!'}</p>
              <p className="mt-2">{language === 'en' ? 'For any inquiries, please contact our support team.' : 'لأي استفسارات، يرجى الاتصال بفريق الدعم.'}</p>
              <p className="mt-4">{formatDate(bookingData.createdAt)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blackC">
      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-40">
        <div className="absolute inset-0 bg-gradient-to-b from-dark to-blackC" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <p className="section-subtitle">{language === 'en' ? 'Premium Service' : 'خدمة متميزة'}</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-cream mb-6">
              {t.bookRide}
            </h1>
            <div className="divider-gold mx-auto" />
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-16">
            {[1, 2, 3, 4].map((s, index) => (
              <React.Fragment key={s}>
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${step >= s
                      ? 'bg-primary text-blackC'
                      : 'bg-charcoal text-gray-light border border-gray'
                      }`}
                  >
                    {s}
                  </div>
                  <span
                    className={`text-sm hidden sm:block ${step >= s ? 'text-cream' : 'text-gray-light'
                      } ${language === 'en' ? 'ml-3' : 'mr-3'}`}
                  >
                    {s === 1 && (language === 'en' ? 'Trip Details' : 'تفاصيل الرحلة')}
                    {s === 2 && (language === 'en' ? 'Schedule' : 'الجدول')}
                    {s === 3 && (language === 'en' ? 'Contact Info' : 'معلومات الاتصال')}
                    {s === 4 && (language === 'en' ? 'Payment' : 'الدفع')}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    className={`w-12 sm:w-16 h-px mx-2 sm:mx-3 ${step > s ? 'bg-primary' : 'bg-gray'
                      }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onFocusCapture={handleFormStart}
            className="bg-dark border border-gray-dark p-8 lg:p-12"
          >
            {/* Step 1: Trip Details */}
            {step === 1 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-display text-2xl text-cream mb-8">
                  {language === 'en' ? 'Trip Details' : 'تفاصيل الرحلة'}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.bookingType}</label>
                    <select
                      name="bookingType"
                      value={formData.bookingType}
                      onChange={handleChange}
                      className="select-luxury"
                      required
                    >
                      <option value="fromAirport">{t.fromAirport}</option>
                      <option value="toAirport">{t.toAirport}</option>
                      <option value="insideCairo">{t.insideCairo}</option>
                      <option value="anywhere">{t.anywhere}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.tripType}</label>
                    <select
                      name="tripType"
                      value={formData.tripType}
                      onChange={handleChange}
                      className="select-luxury"
                      required
                    >
                      <option value="oneWay">{t.oneWay}</option>
                      <option value="roundTrip">{t.roundTrip}</option>
                    </select>
                  </div>
                </div>

                {/* Pickup Location - Autocomplete */}
                <div className="relative" ref={pickupInputRef}>
                  <label className="block text-sm text-gray-silver mb-2">{t.pickupLocation}</label>
                  <input
                    type="text"
                    name="pickupLocation"
                    value={formData.pickupLocation}
                    onChange={handleChange}
                    onFocus={() => {
                      filterPickupSuggestions(formData.pickupLocation);
                      setShowPickupSuggestions(true);
                    }}
                    className="input-luxury"
                    placeholder={language === 'en' ? 'Type or select pickup location' : 'اكتب أو اختر موقع الاستلام'}
                    required
                    autoComplete="off"
                  />
                  {showPickupSuggestions && filteredPickupSuggestions.length > 0 && (
                    <div
                      ref={pickupDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    >
                      {filteredPickupSuggestions.map((location, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handlePickupSelect(location)}
                          className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0 text-cream"
                        >
                          {location}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dropoff Location - Autocomplete */}
                <div className="relative" ref={dropoffInputRef}>
                  <label className="block text-sm text-gray-silver mb-2">{t.dropoffLocation}</label>
                  <input
                    type="text"
                    name="dropoffLocation"
                    value={formData.dropoffLocation}
                    onChange={handleChange}
                    onFocus={() => {
                      if (formData.pickupLocation || dropoffLocations.length > 0) {
                        filterDropoffSuggestions(formData.dropoffLocation);
                        setShowDropoffSuggestions(true);
                      }
                    }}
                    className="input-luxury"
                    placeholder={language === 'en' ? 'Type or select dropoff location' : 'اكتب أو اختر موقع الوصول'}
                    required
                    disabled={!formData.pickupLocation}
                    autoComplete="off"
                  />
                  {showDropoffSuggestions && filteredDropoffSuggestions.length > 0 && formData.pickupLocation && (
                    <div
                      ref={dropoffDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-dark border border-gray-dark rounded-lg shadow-xl max-h-60 overflow-y-auto"
                    >
                      {filteredDropoffSuggestions.map((location, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleDropoffSelect(location)}
                          className="w-full px-4 py-3 text-left hover:bg-charcoal transition-colors border-b border-gray-dark last:border-b-0 text-cream"
                        >
                          {location}
                        </button>
                      ))}
                    </div>
                  )}
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.passengers}</label>
                    <input
                      type="number"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleChange}
                      min="1"
                      className="input-luxury"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.luggage}</label>
                    <input
                      type="number"
                      name="luggage"
                      value={formData.luggage}
                      onChange={handleChange}
                      min="0"
                      className="input-luxury"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-silver mb-4">{t.carType}</label>

                  {/* Hidden input for form validation */}
                  <input
                    type="hidden"
                    name="carType"
                    value={formData.carType}
                    required
                  />

                  {!formData.pickupLocation || !formData.dropoffLocation ? (
                    <div className="p-6 bg-charcoal border border-gray-dark rounded-lg text-center">
                      <p className="text-gray-light">
                        {language === 'en' ? 'Please select pickup and dropoff locations first' : 'يرجى اختيار مواقع الاستلام والوصول أولاً'}
                      </p>
                    </div>
                  ) : availableCars.length === 0 ? (
                    <div className="p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                      <p className="text-yellow-400 text-sm">
                        {language === 'en'
                          ? 'No presets available for this route. Support will contact you with pricing.'
                          : 'لا توجد قوائم مسبقة لهذا المسار. سيتواصل معك الدعم لتحديد السعر.'}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {availableCars.map((car) => {
                        // Find preset price for this car type
                        // Priority: specific car type preset > 'all' type preset
                        let carPreset = routePresets.find(p => p.carType === car.type);
                        if (!carPreset) {
                          carPreset = routePresets.find(p => p.carType === 'all');
                        }
                        const price = carPreset ? carPreset.price : null;
                        const isSelected = formData.carType === car.type;

                        return (
                          <button
                            key={car._id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, carType: car.type }));
                              // Trigger price update
                              if (routePresets.length > 0) {
                                let preset = routePresets.find(p => p.carType === car.type);
                                if (!preset) {
                                  preset = routePresets.find(p => p.carType === 'all');
                                }
                                if (preset) {
                                  setSelectedPreset(preset);
                                  setPresetPrice(preset.price);
                                } else {
                                  setSelectedPreset(null);
                                  setPresetPrice(null);
                                }
                              }
                            }}
                            className={`relative group bg-dark border-2 rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/50 ${isSelected
                              ? 'border-primary shadow-lg shadow-primary/20'
                              : 'border-gray-dark hover:shadow-lg'
                              }`}
                          >
                            {/* Selected Indicator */}
                            {isSelected && (
                              <div className="absolute top-3 right-3 z-10 bg-primary text-blackC w-8 h-8 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}

                            {/* Car Image */}
                            <div className="relative h-48 overflow-hidden bg-charcoal">
                              {car.images && car.images.length > 0 ? (
                                <img
                                  src={car.images[0].url}
                                  alt={car.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <svg className="w-16 h-16 text-gray" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
                                  </svg>
                                </div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-blackC via-transparent to-transparent opacity-60" />
                              <div className="absolute top-3 left-3 bg-primary/90 text-blackC px-3 py-1 text-xs font-semibold tracking-wider uppercase rounded">
                                {car.type}
                              </div>
                            </div>

                            {/* Car Details */}
                            <div className="p-4">
                              <h3 className="font-display text-lg text-cream mb-2">{car.name}</h3>
                              {car.model && (
                                <p className="text-gray-silver text-sm mb-3">{car.model}</p>
                              )}

                              <div className="flex items-center gap-4 text-sm mb-3">
                                <div className="flex items-center gap-1.5 text-gray-light">
                                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                  <span>{car.passengerCapacity} {t.passengers}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-gray-light">
                                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                  <span>{car.luggageCapacity} {language === 'en' ? 'Luggage' : 'أمتعة'}</span>
                                </div>
                              </div>

                              {/* Price Display */}
                              {price && (
                                <div className="pt-3 border-t border-gray-dark">
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-light text-sm">{language === 'en' ? 'Price' : 'السعر'}</span>
                                    <span className="text-primary font-bold text-lg">{getDisplayPrice(price) || price} EGP</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Message before car selection */}
                  {formData.pickupLocation && formData.dropoffLocation && !formData.carType && availableCars.length > 0 && (
                    <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-gray-light">
                          {language === 'en'
                            ? 'Please select a vehicle to see the price for your route.'
                            : 'يرجى اختيار سيارة لمعرفة السعر لمسارك.'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Preset Price Display or Custom Message - Only after car is selected */}
                  {formData.pickupLocation && formData.dropoffLocation && formData.carType && (
                    <div className={`mt-4 p-4 rounded-lg border ${presetPrice ? 'bg-primary/10 border-primary/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                      {presetPrice ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-light mb-1">
                              {language === 'en' ? 'Route Preset Selected' : 'تم اختيار مسار مسبق'}
                            </p>
                            <p className="text-xs text-gray-silver">
                              {formData.pickupLocation} → {formData.dropoffLocation}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-light mb-1">
                              {language === 'en' ? 'Price' : 'السعر'}
                              {formData.tripType === 'roundTrip' && (
                                <span className="text-xs text-gray-silver ml-1">
                                  ({language === 'en' ? 'Round Trip' : 'ذهاب وإياب'})
                                </span>
                              )}
                            </p>
                            <p className="text-2xl font-display text-primary font-bold">
                              {getDisplayPrice(presetPrice) || presetPrice} EGP
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-yellow-400 mb-1">
                              {language === 'en' ? 'Custom Route' : 'مسار مخصص'}
                            </p>
                            <p className="text-xs text-gray-silver">
                              {language === 'en'
                                ? 'After confirming your booking, our support team will contact you to provide all trip details and pricing.'
                                : 'بعد تأكيد حجزك، سيتواصل معك فريق الدعم لتقديم جميع تفاصيل الرحلة والأسعار.'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Policy agreement - clickable row toggles checkbox; custom box so checked state is always visible */}
                <div
                  role="checkbox"
                  tabIndex={0}
                  onClick={() => setPolicyAgreed(prev => !prev)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPolicyAgreed(prev => !prev); } }}
                  className="flex items-start gap-2 pt-4 border-t border-gray-dark cursor-pointer select-none"
                  aria-label={language === 'en' ? 'I agree to the Privacy Policy' : 'أوافق على سياسة الخصوصية'}
                  aria-checked={policyAgreed}
                >
                  <span
                    className="w-4 h-4 flex-shrink-0 mt-0.5 border-2 rounded flex items-center justify-center text-white text-xs font-bold"
                    style={{
                      borderColor: policyAgreed ? 'var(--color-primary)' : 'rgb(156 163 175)',
                      backgroundColor: policyAgreed ? 'var(--color-primary)' : 'transparent',
                    }}
                    aria-hidden
                  >
                    {policyAgreed ? '✓' : ''}
                  </span>
                  <span id="policyAgree-desc" className="text-sm text-gray-silver flex items-start gap-1 flex-wrap">
                    {language === 'en' ? 'I agree to the ' : 'أوافق على '}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}
                    </Link>
                  </span>
                </div>
              </div>
            )}

            {/* Step 2: Schedule */}
            {step === 2 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-display text-2xl text-cream mb-8">
                  {language === 'en' ? 'Schedule Your Trip' : 'حدد موعد رحلتك'}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.date}</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="input-luxury"
                      required
                    />
                    <p className="text-xs text-gray-silver mt-1">
                      {language === 'en' ? 'Cannot select past dates' : 'لا يمكن اختيار التواريخ السابقة'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-silver mb-2">{t.time}</label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className="input-luxury"
                      required
                    />
                  </div>
                </div>

                {formData.tripType === 'roundTrip' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-gray-dark">
                    <div>
                      <label className="block text-sm text-gray-silver mb-2">{t.returnDate}</label>
                      <input
                        type="date"
                        name="returnDate"
                        value={formData.returnDate}
                        onChange={handleChange}
                        min={formData.date || new Date().toISOString().split('T')[0]}
                        className="input-luxury"
                      />
                      <p className="text-xs text-gray-silver mt-1">
                        {language === 'en' ? 'Must be on or after the departure date' : 'يجب أن يكون في نفس تاريخ الذهاب أو بعده'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-silver mb-2">{t.returnTime}</label>
                      <input
                        type="time"
                        name="returnTime"
                        value={formData.returnTime}
                        onChange={handleChange}
                        className="input-luxury"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-gray-silver mb-2">{t.notes}</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    className="textarea-luxury"
                    placeholder={language === 'en' ? 'Flight number, special requirements, etc.' : 'رقم الرحلة، متطلبات خاصة، إلخ.'}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Contact Info */}
            {step === 3 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-display text-2xl text-cream mb-8">
                  {language === 'en' ? 'Contact Information' : 'معلومات الاتصال'}
                </h2>

                <div>
                  <label className="block text-sm text-gray-silver mb-2">{t.fullName}</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="input-luxury"
                    placeholder={language === 'en' ? 'Enter your full name' : 'أدخل اسمك الكامل'}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-gray-silver mb-2">
                      {t.mobile} <span className="text-yellow-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="input-luxury"
                      placeholder="+20 100 000 0000"
                      required
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-silver mb-2">
                      {language === 'en' ? 'Email' : 'البريد الإلكتروني'} <span className="text-gray-silver text-xs">({language === 'en' ? 'Optional' : 'اختياري'})</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-luxury"
                      placeholder={language === 'en' ? 'your.email@example.com' : 'بريدك@example.com'}
                      dir="ltr"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-silver mb-2">{t.whatsapp}</label>
                  <input
                    type="tel"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className="input-luxury"
                    placeholder="+20 100 000 0000"
                    dir="ltr"
                  />
                </div>

                {/* Summary */}
                <div className="bg-blackC p-6 border border-gray-dark">
                  <h3 className="font-display text-lg text-primary mb-4">
                    {language === 'en' ? 'Booking Summary' : 'ملخص الحجز'}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-light">{t.bookingType}:</span>
                      <span className="text-cream">
                        {formData.bookingType === 'fromAirport' && t.fromAirport}
                        {formData.bookingType === 'toAirport' && t.toAirport}
                        {formData.bookingType === 'insideCairo' && t.insideCairo}
                        {formData.bookingType === 'anywhere' && t.anywhere}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-light">{t.tripType}:</span>
                      <span className="text-cream">
                        {formData.tripType === 'oneWay' ? t.oneWay : t.roundTrip}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-light">{t.pickupLocation}:</span>
                      <span className="text-cream">{formData.pickupLocation}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-light">{t.dropoffLocation}:</span>
                      <span className="text-cream">{formData.dropoffLocation}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-light">{t.date}:</span>
                      <span className="text-cream">{formData.date} - {formData.time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-light">{t.passengers}:</span>
                      <span className="text-cream">{formData.passengers}</span>
                    </div>
                    {presetPrice && (
                      <div className="flex justify-between pt-2 border-t border-gray-dark">
                        <span className="text-gray-light font-medium">
                          {language === 'en' ? 'Estimated Price' : 'السعر المقدر'}
                          {formData.tripType === 'roundTrip' && (
                            <span className="text-xs text-gray-silver ml-1">
                              ({language === 'en' ? 'Round Trip' : 'ذهاب وإياب'})
                            </span>
                          )}
                          :
                        </span>
                        <span className="text-primary font-bold text-lg">{getDisplayPrice(presetPrice) || presetPrice} EGP</span>
                      </div>
                    )}
                    {!presetPrice && formData.pickupLocation && formData.dropoffLocation && (
                      <div className="pt-2 border-t border-gray-dark">
                        <p className="text-xs text-yellow-400">
                          {language === 'en'
                            ? 'Price will be confirmed by support team'
                            : 'سيتم تأكيد السعر من قبل فريق الدعم'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-display text-2xl text-cream mb-8">
                  {language === 'en' ? 'Payment & Confirmation' : 'الدفع والتأكيد'}
                </h2>

                {/* Price Summary */}
                <div className="bg-blackC border border-gray-dark rounded-lg p-6">
                  <h3 className="font-display text-lg text-primary mb-4">
                    {language === 'en' ? 'Price Details' : 'تفاصيل السعر'}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-light">
                        {language === 'en' ? 'Trip Type' : 'نوع الرحلة'}:
                      </span>
                      <span className="text-cream">
                        {formData.tripType === 'oneWay'
                          ? (language === 'en' ? 'One Way' : 'ذهاب فقط')
                          : (language === 'en' ? 'Round Trip' : 'ذهاب وإياب')
                        }
                      </span>
                    </div>
                    {presetPrice ? (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-light">
                            {language === 'en' ? 'Base Price' : 'السعر الأساسي'}:
                          </span>
                          <span className="text-cream">{presetPrice} EGP</span>
                        </div>
                        {formData.tripType === 'roundTrip' && settings && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-silver">
                              {language === 'en' ? 'Round Trip Multiplier' : 'مضاعف الذهاب والإياب'} ({settings.roundTripMultiplier}x):
                            </span>
                            <span className="text-gray-silver">
                              {presetPrice} × {settings.roundTripMultiplier}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-3 border-t border-gray-dark">
                          <span className="text-primary font-bold text-lg">
                            {language === 'en' ? 'Total Price' : 'السعر الإجمالي'}:
                          </span>
                          <span className="text-primary font-bold text-2xl">
                            {getDisplayPrice(presetPrice)} EGP
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="pt-3 border-t border-gray-dark">
                        <p className="text-yellow-400 text-sm">
                          {language === 'en'
                            ? 'Custom route - Price will be confirmed by support team'
                            : 'مسار مخصص - سيتم تأكيد السعر من قبل فريق الدعم'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Options */}
                <div className="space-y-4">
                  <h3 className="font-display text-lg text-cream">
                    {language === 'en' ? 'Choose Payment Option' : 'اختر طريقة الدفع'}
                  </h3>

                  {/* Pay Now Option - Only show if preset price is available */}
                  {presetPrice && (
                    <label
                      className={`block p-5 border-2 rounded-lg cursor-pointer transition-all ${paymentOption === 'payNow'
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-dark bg-blackC hover:border-primary/50'
                        }`}
                    >
                      <div className="flex items-start gap-4">
                        <input
                          type="radio"
                          name="paymentOption"
                          value="payNow"
                          checked={paymentOption === 'payNow'}
                          onChange={(e) => setPaymentOption(e.target.value)}
                          className="mt-1 w-5 h-5 text-primary focus:ring-primary focus:ring-offset-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            <span className="font-semibold text-cream">
                              {language === 'en' ? 'Pay Now' : 'ادفع الآن'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-silver">
                            {language === 'en'
                              ? 'Complete payment online using bank transfer, mobile wallet, or other methods'
                              : 'أكمل الدفع عبر الإنترنت باستخدام التحويل البنكي أو المحفظة الإلكترونية أو طرق أخرى'}
                          </p>
                          {paymentOption === 'payNow' && (
                            <div className="mt-4 pt-4 border-t border-gray-dark space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-light mb-2">
                                  {language === 'en' ? 'Select Payment Method' : 'اختر طريقة الدفع'}
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {paymentMethods.filter(m => m.isActive).map((method) => (
                                    <label
                                      key={method.id}
                                      className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${quickPayMethod === method.id
                                        ? 'border-primary bg-primary/10'
                                        : 'border-gray-dark bg-charcoal hover:border-primary/50'
                                        }`}
                                    >
                                      <input
                                        type="radio"
                                        name="quickPayMethod"
                                        value={method.id}
                                        checked={quickPayMethod === method.id}
                                        onChange={(e) => setQuickPayMethod(e.target.value)}
                                        className="w-4 h-4 text-primary focus:ring-primary"
                                        required={paymentOption === 'payNow'}
                                      />
                                      <div className="text-primary">{method.icon}</div>
                                      <span className="text-cream font-medium flex-1">{method.title}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>

                              {/* Payment Method Details */}
                              {quickPayMethod && (() => {
                                const selectedMethod = paymentMethods.find(m => m.id === quickPayMethod);
                                return selectedMethod ? (
                                  <div className="bg-charcoal border border-primary/30 rounded-lg p-5 space-y-4">
                                    <div className="flex items-start gap-3">
                                      <div className="text-primary mt-0.5 flex-shrink-0">{selectedMethod.icon}</div>
                                      <div className="flex-1">
                                        <h4 className="text-cream font-semibold text-lg mb-2">{selectedMethod.title}</h4>
                                        <p className="text-gray-silver text-sm leading-relaxed">{selectedMethod.description}</p>
                                      </div>
                                    </div>

                                    <div className="bg-blackC border border-gray-dark rounded-lg p-4">
                                      <p className="text-xs text-gray-silver mb-3 font-medium uppercase tracking-wider">
                                        {language === 'en' ? 'Payment Details' : 'تفاصيل الدفع'}
                                      </p>
                                      <pre className="text-primary text-sm whitespace-pre-wrap font-body leading-relaxed" dir="ltr">
                                        {selectedMethod.details}
                                      </pre>
                                    </div>
                                  </div>
                                ) : null;
                              })()}

                              <div>
                                <label className="block text-sm font-medium text-gray-light mb-2">
                                  {language === 'en' ? 'Upload Payment Proof' : 'رفع إثبات الدفع'}
                                </label>
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setQuickPayProofImage(e.target.files[0])}
                                    className="hidden"
                                    id="quickPayProof"
                                    required={paymentOption === 'payNow'}
                                  />
                                  <label
                                    htmlFor="quickPayProof"
                                    className="flex items-center justify-center gap-3 w-full p-6 border-2 border-dashed border-gray-dark hover:border-primary/50 cursor-pointer transition-colors rounded-lg bg-blackC"
                                  >
                                    {quickPayProofImage ? (
                                      <div className="text-center">
                                        <svg className="w-6 h-6 text-primary mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <p className="text-cream text-sm">{quickPayProofImage.name}</p>
                                        <p className="text-gray-silver text-xs mt-1">{language === 'en' ? 'Click to change' : 'انقر للتغيير'}</p>
                                      </div>
                                    ) : (
                                      <div className="text-center">
                                        <svg className="w-6 h-6 text-gray-light mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <p className="text-gray-silver text-sm">{language === 'en' ? 'Click to upload payment proof' : 'انقر لرفع إثبات الدفع'}</p>
                                        <p className="text-gray-light text-xs mt-1">{language === 'en' ? 'PNG, JPG up to 10MB' : 'PNG, JPG حتى 10MB'}</p>
                                      </div>
                                    )}
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  )}

                  {/* Pay Cash Option */}
                  <label
                    className={`block p-5 border-2 rounded-lg cursor-pointer transition-all ${paymentOption === 'payCash'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-dark bg-blackC hover:border-primary/50'
                      }`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="radio"
                        name="paymentOption"
                        value="payCash"
                        checked={paymentOption === 'payCash'}
                        onChange={(e) => setPaymentOption(e.target.value)}
                        className="mt-1 w-5 h-5 text-primary focus:ring-primary focus:ring-offset-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="font-semibold text-cream">
                            {language === 'en' ? 'Pay Cash' : 'الدفع نقداً'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-silver">
                          {language === 'en'
                            ? 'Pay in cash directly to the driver upon arrival'
                            : 'ادفع نقداً مباشرة للسائق عند الوصول'}
                        </p>
                      </div>
                    </div>
                  </label>

                  {/* Contact Support Option */}
                  <label
                    className={`block p-5 border-2 rounded-lg cursor-pointer transition-all ${paymentOption === 'contactSupport'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-dark bg-blackC hover:border-primary/50'
                      }`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="radio"
                        name="paymentOption"
                        value="contactSupport"
                        checked={paymentOption === 'contactSupport'}
                        onChange={(e) => setPaymentOption(e.target.value)}
                        className="mt-1 w-5 h-5 text-primary focus:ring-primary focus:ring-offset-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          <span className="font-semibold text-cream">
                            {language === 'en' ? 'Contact Support' : 'اتصل بالدعم'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-silver">
                          {language === 'en'
                            ? 'Our support team will contact you to arrange payment details'
                            : 'سيتصل بك فريق الدعم لترتيب تفاصيل الدفع'}
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-8 border-t border-gray-dark">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn-secondary"
                >
                  {language === 'en' ? 'Previous' : 'السابق'}
                </button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!isStepValid()}
                  className={`btn-primary ${!isStepValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {language === 'en' ? 'Continue' : 'متابعة'}
                  <svg className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !paymentOption}
                  className="btn-primary"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                      {language === 'en' ? 'Submitting...' : 'جاري الإرسال...'}
                    </>
                  ) : (
                    <>
                      {language === 'en' ? 'Confirm Booking' : 'تأكيد الحجز'}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Book;